<script setup lang="ts">
import { computed } from 'vue';

interface Props {
   primaryColor?: string | null;
   secondaryColor?: string | null;
   size?: string;
}

const props = withDefaults(defineProps<Props>(), {
   primaryColor: undefined,
   secondaryColor: undefined,
   size: '27px',
});

// Map color names to hex values for legacy support
const colorNameToHex: Record<string, string> = {
   // Primary colors
   red: '#ef4444',
   blue: '#3b82f6',
   green: '#22c55e',
   yellow: '#facc15',
   orange: '#f97316',
   purple: '#a855f7',
   pink: '#ec4899',

   // Neutral colors
   white: '#ffffff',
   black: '#1f2937',
   gray: '#9ca3af',

   // Light variants
   'light-blue': '#7dd3fc',
   'light-green': '#6ee7b7',
   'light-red': '#fca5a5',
   'light-yellow': '#fef08a',

   // Dark variants
   'dark-blue': '#1e40af',
   'dark-green': '#166534',
   'dark-red': '#991b1b',

   // Team-specific colors
   navy: '#1e3a8a',
   maroon: '#7f1d1d',
   teal: '#14b8a6',
   gold: '#fbbf24',
   silver: '#d1d5db',
};

// Normalize color to hex format
const normalizeColor = (color: string | null | undefined, fallback: string = '#808080'): string => {
   if (!color) return fallback;

   // Check if it's a named color from our map
   const normalizedName = color.toLowerCase().trim();
   if (colorNameToHex[normalizedName]) {
      return colorNameToHex[normalizedName];
   }

   // If it's already a hex color with #
   if (color.startsWith('#')) return color;

   // If it's hex without #
   if (/^[0-9A-Fa-f]{6}$/.test(color)) return `#${color}`;

   // Return as-is (could be a named CSS color like 'red')
   return color;
};

const primaryFill = computed(() => {
   return normalizeColor(props.primaryColor, '#808080');
});

const secondaryFill = computed(() => {
   if (!props.secondaryColor) return primaryFill.value;
   return normalizeColor(props.secondaryColor, '#404040');
});

// Generate unique ID for gradient (needed when multiple instances on same page)
const gradientId = computed(() => `silk-gradient-${Math.random().toString(36).substring(2, 11)}`);
</script>

<template>
   <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 90 80.1"
      :style="{ width: size, height: 'auto' }"
      class="stroke-black/50"
      stroke-width="3"
   >
      <!-- Define the two-color gradient with hard stop at 50% -->
      <defs>
         <linearGradient :id="gradientId" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" :stop-color="primaryFill" />
            <stop offset="50%" :stop-color="primaryFill" />
            <stop offset="50%" :stop-color="secondaryFill" />
            <stop offset="100%" :stop-color="secondaryFill" />
         </linearGradient>
      </defs>

      <path
         d="m33.6 0-1.4.6-14.7 5.9-.5.2-.4.4L2.1 21.5l-2.1 2 2.3 1.8 15.5 12.2 1.7 1.4 1.6-1.5.3-.3v43H69V37.3l1.7 1.7 1.8-1.4 15.2-12.2 2.3-1.8-2.1-2L73.4 7.4l-.4-.3-.5-.2L57.7.7 56.3.1l-1.2 1C52.3 3.4 48.7 4.7 45 4.7c-3.7 0-7.3-1.3-10.2-3.7l-1.2-1z"
         :fill="`url(#${gradientId})`"
         stroke-linejoin="miter"
      />
   </svg>
</template>
